import { Graphics } from 'pixi.js';
import type { Vec2 } from './physics.js';

export type JoystickZone = 'left' | 'right' | 'full';

const RADIUS    = 55; // outer ring radius in CSS px
const KNOB_R    = 22; // inner knob radius in CSS px
const DEAD_ZONE = 0.12; // ignore tiny touches

export class Joystick {
  zone: JoystickZone;

  // Normalized world-space direction (screen-space: +y down)
  direction: Vec2;
  active = false;

  private pointerId: number | null = null;
  private originX = 0;
  private originY = 0;
  private knobDX  = 0;
  private knobDY  = 0;

  constructor(zone: JoystickZone, defaultDir: Vec2) {
    this.zone      = zone;
    this.direction = defaultDir;
  }

  handlePointerDown(e: PointerEvent, screenW: number, canvas: HTMLCanvasElement): boolean {
    if (this.pointerId !== null) return false;

    const isLeft = e.clientX < screenW / 2;
    if (this.zone === 'left'  && !isLeft) return false;
    if (this.zone === 'right' &&  isLeft) return false;

    this.pointerId = e.pointerId;
    this.originX   = e.clientX;
    this.originY   = e.clientY;
    this.knobDX    = 0;
    this.knobDY    = 0;
    this.active    = true;
    canvas.setPointerCapture(e.pointerId);
    return true;
  }

  handlePointerMove(e: PointerEvent): boolean {
    if (e.pointerId !== this.pointerId) return false;

    const dx   = e.clientX - this.originX;
    const dy   = e.clientY - this.originY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > DEAD_ZONE * RADIUS) {
      const clamped = Math.min(dist, RADIUS);
      this.knobDX   = (dx / dist) * clamped;
      this.knobDY   = (dy / dist) * clamped;
      this.direction = { x: dx / dist, y: dy / dist };
    }
    return true;
  }

  handlePointerUp(e: PointerEvent): boolean {
    if (e.pointerId !== this.pointerId) return false;
    this.pointerId = null;
    this.active    = false;
    this.knobDX    = 0;
    this.knobDY    = 0;
    // direction intentionally kept: boat holds last heading
    return true;
  }

  get baseX(): number { return this.originX; }
  get baseY(): number { return this.originY; }
  get kx(): number    { return this.originX + this.knobDX; }
  get ky(): number    { return this.originY + this.knobDY; }
}

export function drawJoysticks(
  g: Graphics,
  ...joysticks: Joystick[]
): void {
  g.clear();
  for (const j of joysticks) {
    if (!j.active) continue;
    // Outer ring
    g.circle(j.baseX, j.baseY, RADIUS);
    g.fill({ color: 0x000000, alpha: 0.15 });
    g.circle(j.baseX, j.baseY, RADIUS);
    g.stroke({ color: 0xffffff, alpha: 0.30, width: 2 });
    // Knob
    g.circle(j.kx, j.ky, KNOB_R);
    g.fill({ color: 0xffffff, alpha: 0.40 });
    g.circle(j.kx, j.ky, KNOB_R);
    g.stroke({ color: 0xffffff, alpha: 0.60, width: 1.5 });
  }
}

// Draw static "hint" zones when no finger is active — subtle half-circle indicators
export function drawJoystickHints(
  g: Graphics,
  screenW: number,
  screenH: number,
): void {
  g.clear();
  const y  = screenH - 90;
  const r  = 38;
  const alpha = 0.12;

  // Left hint
  g.circle(screenW * 0.22, y, r);
  g.fill({ color: 0xffffff, alpha });
  g.circle(screenW * 0.22, y, r);
  g.stroke({ color: 0xffffff, alpha: alpha * 2, width: 1.5 });

  // Right hint
  g.circle(screenW * 0.78, y, r);
  g.fill({ color: 0xffffff, alpha });
  g.circle(screenW * 0.78, y, r);
  g.stroke({ color: 0xffffff, alpha: alpha * 2, width: 1.5 });

  // Labels
  // (Pixi Text in Graphics is not possible; labels are handled by hud.ts)
}
